/**
 * TypeScript types for CDB-TQS Documents API
 * These types match the backend API response from cdb_tqs
 */
import type { DocumentVideoInfo } from '@/services/cdb-tqs/video'

/**
 * Raw `tqs.documents.status`. `uploaded` appears after a reprocess and
 * `markdown_ready` between conversion and chunking; both still mean "not done
 * yet" (cdb_tqs#1515).
 */
/** What a document is, as `GET /documents?media_type=` filters it (#1601). */
export type DocumentMediaType = 'document' | 'image' | 'video'

export type DocumentStatus =
  | 'pending'
  | 'uploaded'
  | 'processing'
  | 'markdown_ready'
  | 'completed'
  | 'failed'

/** Whether a document still has to be processed — the states worth polling. */
export function isDocumentInProgress(status: string | null | undefined): boolean {
  return status === 'pending' || status === 'uploaded' || status === 'processing' || status === 'markdown_ready'
}

/** The Drive file a document was synced from (`metadata.drive`, cdb_tqs#1515). */
export interface DocumentDriveLink {
  file_id: string
  kind: 'personal_file' | 'attachment'
  version: number | null
  linked_at: string | null
}

/** `image`: the image original's facts (`metadata.image`, cdb_tqs#1515). */
export interface DocumentImageInfo {
  width?: number
  height?: number
  source_mime?: string
  kind?: string
  summary?: string
  [key: string]: unknown
}

/**
 * Columns `GET /api/v1/documents` can order by. Mirrors the whitelist in
 * cdb_tqs `app/api/routes/documents.py::_DOCUMENT_SORT_COLUMNS` — anything else
 * is rejected by the backend with a 422.
 *
 * `title` orders by the displayed name, which falls back to the filename when a
 * document has no title.
 */
export type DocumentSortKey = 'title' | 'knowledge_area' | 'status' | 'created_at'

export type DocumentSortDirection = 'asc' | 'desc'

export interface CdbTqsDocument {
  document_id: string
  job_id: string | null
  title: string
  filename: string
  file_hash: string
  file_size: number
  knowledge_area: string
  knowledge_area_color: string | null
  status: DocumentStatus
  tags: string[]
  source: string | null
  chunks_count: number
  entities_extracted: number
  qa_pairs_count: number
  graph_episode_uuid: string | null
  graph_nodes: number
  graph_edges: number
  auto_verify_tl1?: boolean
  created_at: string
  processed_at: string | null
  /** Why processing failed; only while `status` is `failed` (cdb_tqs#1515). */
  error_message?: string | null
  /** The Drive file this document was synced from, if any (cdb_tqs#1515). */
  drive?: DocumentDriveLink | null
  /** Optional since cdb_tqs#1515 — older backends omit all four. */
  mime_type?: string | null
  /** `video` since the video ingest plan (TQS-1); older backends never send it. */
  media_type?: 'image' | 'document' | 'video' | null
  image?: DocumentImageInfo | null
  /** Facts about a processed video (duration, speakers, AI summary …). */
  video?: DocumentVideoInfo | null
  /** AI-written core statement of an image document. */
  summary?: string | null
  /** Document versions (cdb_tqs#1974); 1 / `current` for a document without a chain. */
  version_number?: number
  version_state?: 'current' | 'superseded' | 'archived'
  version_chain_id?: string | null
  previous_version_id?: string | null
}

export interface CdbTqsDocumentListResponse {
  total: number
  documents: CdbTqsDocument[]
  limit: number
  offset: number
}

/**
 * Document chunk with embedding status
 */
export interface DocumentChunk {
  chunk_id: string
  document_id: string
  chunk_index: number
  content: string
  character_count: number
  has_embedding: boolean
  created_at: string
}

/**
 * Drive file type used in DriveApp
 */
export type DriveFileType = 'pdf' | 'audio' | 'video' | 'image' | 'text' | 'spreadsheet' | 'presentation' | 'other'

export interface DriveFile {
  id: string
  name: string
  fileName: string
  fullPath: string // For cdb_tqs, this will be the documentId
  type: DriveFileType
  size?: number
  updatedAt?: string
  knowledgeArea: string
  knowledgeAreaColor?: string | null
  // Additional cdb_tqs metadata
  status?: DocumentStatus
  chunksCount?: number
  tags?: string[]
}

/**
 * Determines file type based on filename extension
 */
export function getFileTypeFromFilename(filename: string): DriveFileType {
  const extension = filename.toLowerCase().split('.').pop()

  if (!extension) return 'other'

  // PDF files
  if (extension === 'pdf') return 'pdf'

  // Audio files
  if (['mp3', 'wav', 'm4a', 'aac', 'ogg', 'flac', 'wma'].includes(extension)) return 'audio'

  // Video files
  if (['mp4', 'mov', 'mkv', 'webm', 'avi', 'wmv', 'flv', 'm4v'].includes(extension)) return 'video'

  // Image files
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'tiff'].includes(extension)) return 'image'

  // Text files
  if (['txt', 'md', 'xml', 'json', 'csv', 'doc', 'docx', 'rtf'].includes(extension)) return 'text'

  // Spreadsheet files
  if (['xls', 'xlsx', 'xlsm', 'xlsb'].includes(extension)) return 'spreadsheet'

  // Presentation files
  if (['ppt', 'pptx'].includes(extension)) return 'presentation'

  return 'other'
}

/**
 * Maps a CDB-TQS document to DriveFile format
 * @param doc CDB-TQS document from API
 * @returns DriveFile for use in DriveApp
 */
export function cdbTqsDocumentToDriveFile(doc: CdbTqsDocument): DriveFile {
  const fileType = getFileTypeFromFilename(doc.filename)

  return {
    id: doc.document_id,
    name: doc.title || doc.filename.replace(/\.[^.]+$/, ''),
    fileName: doc.filename,
    fullPath: doc.document_id, // Use documentId as fullPath for compatibility
    type: fileType,
    size: doc.file_size,
    updatedAt: doc.processed_at || doc.created_at,
    knowledgeArea: doc.knowledge_area,
    knowledgeAreaColor: doc.knowledge_area_color,
    // Additional metadata
    status: doc.status,
    chunksCount: doc.chunks_count,
    tags: doc.tags,
  }
}
